import { useState, useEffect } from 'react';
import { db } from '../lib/firebase';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { ArrowUpRight, ArrowDownLeft, ChevronLeft, Search, History } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import StatusBadge from '../components/StatusBadge';
import BottomNav from '../components/BottomNav';

export default function Transactions() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) return;
    const combined = { loans: [], repayments: [] };

    const unsubLoans = onSnapshot(
      query(collection(db, 'loans'), where('userId', '==', currentUser.uid)),
      (snap) => {
        combined.loans = snap.docs.map(d => ({ id: d.id, ...d.data(), type: 'loan' }));
        merge();
      }
    );

    const unsubRepay = onSnapshot(
      query(collection(db, 'repayments'), where('userId', '==', currentUser.uid)),
      (snap) => {
        combined.repayments = snap.docs.map(d => ({ id: d.id, ...d.data(), type: 'repayment' }));
        merge();
      }
    );

    function merge() {
      const all = [...combined.loans, ...combined.repayments]
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setItems(all);
      setLoading(false);
    }

    return () => { unsubLoans(); unsubRepay(); };
  }, [currentUser]);

  const FILTERS = ['all', 'loans', 'repayments', 'pending'];

  const filtered = items.filter(item => {
    const q = searchQuery.toLowerCase();
    const matchSearch = !q ||
      item.amount?.toString().includes(q) ||
      item.amountRequested?.toString().includes(q) ||
      item.status?.toLowerCase().includes(q);
    const matchFilter =
      filter === 'all' ||
      (filter === 'loans' && item.type === 'loan') ||
      (filter === 'repayments' && item.type === 'repayment') ||
      (filter === 'pending' && item.status?.includes('pending'));
    return matchSearch && matchFilter;
  });

  const amount = (item) =>
    (item.amount || item.amountRequested || 0).toLocaleString();
  const isCredit = (item) => item.type === 'repayment' || item.status === 'approved';

  return (
    <div className="min-h-screen bg-stripe-bg pb-24">
      {/* Header */}
      <header className="page-header">
        <div className="page-header-content">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="btn-icon">
              <ChevronLeft size={18} />
            </button>
            <div>
              <h1 className="text-base font-bold text-stripe-dark tracking-tight">Transaction History</h1>
              <p className="text-xs text-stripe-light font-medium hidden sm:block">Your loan &amp; repayment history</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-6 space-y-4">
        {/* Search + Filter */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-stripe-light" />
            <input
              type="text"
              placeholder="Search transactions…"
              className="s-input pl-10 text-sm"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="flex gap-2 overflow-x-auto">
            {FILTERS.map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-2 rounded-xl text-xs font-semibold capitalize transition-all whitespace-nowrap ${
                  filter === f
                    ? 'bg-stripe-dark text-white shadow-stripe-sm'
                    : 'bg-white text-stripe-slate border border-stripe-border hover:border-stripe-dark/30'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {/* Results */}
        {loading ? (
          <div className="empty-state s-card">
            <div className="w-10 h-10 border-2 border-stripe-border border-t-primary-500 rounded-full animate-spin mb-3" />
            <p className="text-sm text-stripe-slate font-medium">Loading transactions…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="s-card">
            <div className="empty-state">
              <div className="w-14 h-14 bg-stripe-bg rounded-2xl flex items-center justify-center mb-4 border border-stripe-border">
                <History size={24} className="text-stripe-light" />
              </div>
              <h3 className="text-base font-semibold text-stripe-dark mb-1">No transactions found</h3>
              <p className="text-sm text-stripe-slate">Transactions will appear here once you apply for a loan.</p>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map(item => (
              <div key={item.id}
                className="s-card flex items-center justify-between gap-4 hover:border-stripe-dark/20 transition-all cursor-pointer active:scale-[0.99]"
              >
                <div className="flex items-center gap-3.5">
                  {/* Icon */}
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                    isCredit(item) ? 'bg-green-50 text-green-600' :
                    item.status === 'pending_admin' ? 'bg-blue-50 text-stripe-blue' :
                    'bg-stripe-bg text-stripe-slate'
                  }`}>
                    {item.type === 'repayment'
                      ? <ArrowDownLeft size={18} />
                      : <ArrowUpRight size={18} />}
                  </div>

                  {/* Details */}
                  <div>
                    <p className="text-sm font-semibold text-stripe-dark">
                      {item.type === 'repayment'
                        ? `Repayment · ${item.option || ''}`
                        : 'Loan request'}
                    </p>
                    <p className="text-xs text-stripe-slate font-medium">
                      {new Date(item.createdAt).toLocaleDateString(undefined, {
                        month: 'short', day: 'numeric', year: 'numeric'
                      })}
                    </p>
                  </div>
                </div>

                {/* Right side */}
                <div className="text-right shrink-0 flex flex-col items-end gap-1.5">
                  <p className={`text-sm font-bold ${isCredit(item) ? 'text-green-600' : 'text-stripe-dark'}`}>
                    {isCredit(item) ? '+' : '−'} LKR {amount(item)}
                  </p>
                  <StatusBadge status={item.status} />
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <BottomNav />
    </div>
  );
}
